import { useSearchParams } from "react-router-dom";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { useForm } from "react-hook-form";
import { X } from "lucide-react";
import MultiSelectTags from "./multi-select-tags";
import {
  professionalStatusLabels,
  jobCommitmentLabels,
  ProfessionalType,
} from "../schema/professional-schema";
import { useEffect } from "react";

// Define the filter form interface
interface ProfessionalFilters {
  status: string;
  job_commitment: string;
  type: string;
  qualification: string[];
}

export default function ProfessionalFiltersOnly() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize filters from URL params
  const initialFilters: ProfessionalFilters = {
    status: searchParams.get("status") || "ALL",
    job_commitment: searchParams.get("job_commitment") || "ALL",
    type: searchParams.get("type") || "ALL",
    qualification: searchParams.get("qualification")?.split(",") || [],
  };

  // Initialize form with values from URL
  const form = useForm<ProfessionalFilters>({
    defaultValues: initialFilters,
  });

  // Update URL when filters change
  const applyFilters = (values: ProfessionalFilters) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("page", "0"); // Reset to first page when filters change

    // Keep search if it exists
    const search = searchParams.get("search");
    if (search) newParams.set("search", search);

    if (values.status !== "ALL") newParams.set("status", values.status);
    else newParams.delete("status");

    if (values.job_commitment !== "ALL")
      newParams.set("job_commitment", values.job_commitment);
    else newParams.delete("job_commitment");

    if (values.type !== "ALL") newParams.set("type", values.type);
    else newParams.delete("type");

    if (values.qualification.length > 0)
      newParams.set("qualification", values.qualification.join(","));
    else newParams.delete("qualification");

    setSearchParams(newParams);
  };

  // Reset all filters
  const handleReset = () => {
    form.reset({
      status: "ALL",
      job_commitment: "ALL",
      type: "ALL",
      qualification: [],
    });
    const newParams = new URLSearchParams();
    newParams.set("page", "0");
    // Keep search if it exists
    const search = searchParams.get("search");
    if (search) newParams.set("search", search);
    setSearchParams(newParams);
  };

  // Update form when URL changes
  useEffect(() => {
    form.reset({
      status: searchParams.get("status") || "ALL",
      job_commitment: searchParams.get("job_commitment") || "ALL",
      type: searchParams.get("type") || "ALL",
      qualification: searchParams.get("qualification")?.split(",") || [],
    });
  }, [searchParams, form]);

  // Check if any filters are applied
  const areFiltersApplied =
    (searchParams.get("status") !== "ALL" && !!searchParams.get("status")) ||
    (searchParams.get("job_commitment") !== "ALL" &&
      !!searchParams.get("job_commitment")) ||
    (searchParams.get("type") !== "ALL" && !!searchParams.get("type")) ||
    !!searchParams.get("qualification");

  return (
    <Form {...form}>
      <div className="flex flex-wrap items-center gap-4">
          <FormField
            control={form.control}
            name="status"
            render={({ field }) => (
              <FormItem>
                <Select
                  value={field.value}
                  onValueChange={(value) => {
                    field.onChange(value);
                    applyFilters({ ...form.getValues(), status: value });
                  }}
                >
                  <FormControl>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="ALL">All Statuses</SelectItem>
                    {Object.entries(professionalStatusLabels).map(
                      ([value, label]) => (
                        <SelectItem key={value} value={value}>
                          {label}
                        </SelectItem>
                      )
                    )}
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="job_commitment"
            render={({ field }) => (
              <FormItem>
                <Select
                  value={field.value}
                  onValueChange={(value) => {
                    field.onChange(value);
                    applyFilters({ ...form.getValues(), job_commitment: value });
                  }}
                >
                  <FormControl>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Job Commitment" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="ALL">All Commitments</SelectItem>
                    {Object.entries(jobCommitmentLabels).map(
                      ([value, label]) => (
                        <SelectItem key={value} value={value}>
                          {label}
                        </SelectItem>
                      )
                    )}
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="type"
            render={({ field }) => (
              <FormItem>
                <Select
                  value={field.value}
                  onValueChange={(value) => {
                    field.onChange(value);
                    applyFilters({ ...form.getValues(), type: value });
                  }}
                >
                  <FormControl>
                    <SelectTrigger className="w-[220px]">
                      <SelectValue placeholder="Professional Type" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="ALL">All Types</SelectItem>
                    {/* Options show the exact DB enum values (ProfessionalType) */}
                    {Object.values(ProfessionalType).map((value) => (
                      <SelectItem key={value} value={value}>
                        {value}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          />

          {areFiltersApplied && (
            <Button
              type="button"
              variant="outline"
              onClick={handleReset}
              className="ml-auto"
            >
              <X className="h-4 w-4 mr-1" />
              Reset
            </Button>
          )}

        {/* Qualifications */}
        {/* <FormField
          control={form.control}
          name="qualification"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <MultiSelectTags
                  value={field.value}
                  onChange={(value) => {
                    field.onChange(value);
                    applyFilters({ ...form.getValues(), qualification: value });
                  }}
                  placeholder="Enter qualification and press Enter"
                />
              </FormControl>
            </FormItem>
          )}
        /> */}
      </div>
    </Form>
  );
}

